import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaArrowRight,
  FaHeadset,
  FaPhoneVolume,
  FaUser,
  FaCommentDots,
} from 'react-icons/fa';
import './Query.css';
import { initFacebookPixel } from '../facebookPixel';

const MAX_MESSAGE_LENGTH = 250;

const Query = () => {
  const [userName, setUserName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    initFacebookPixel(); // Track page view when this page loads
  }, []);

  const validateForm = () => {
    const nextErrors = {};

    if (!userName.trim()) {
      nextErrors.userName = 'Please enter your name.';
    }

    if (phoneNumber.length !== 10) {
      nextErrors.phoneNumber = 'Please enter a valid 10-digit phone number.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePhoneChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(value);

    if (value.length === 10) {
      setErrors((previousErrors) => ({ ...previousErrors, phoneNumber: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const bookingData = {
      parentsName: userName.trim(),
      mobileNumber: phoneNumber,
      qresponse: message.trim(),
      source: 'Badminton-Coaching',
      email: 'hello@machaxi.com',
    };

    try {
      const apiUrl = process.env.REACT_APP_API_URL_GIT;
      const apiKey = process.env.REACT_APP_API_KEY;

      if (!apiUrl || !apiKey) {
        throw new Error('Missing query API configuration.');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        let errorMessage = 'Submission failed.';

        try {
          const errorData = await response.json();
          errorMessage = errorData?.message || errorMessage;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      sessionStorage.setItem('paymentStatus', 'success');
      sessionStorage.setItem('message', 'Our support team will contact you shortly.\n\nThank you!');
      window.location.href = '/thankyou-page';
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to submit',
        text: error.message || 'Please try again or contact us on WhatsApp.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderSupportStrip = (className = '') => (
    <ul className={`query-support-strip ${className}`.trim()}>
      <li>
        <span className="query-support-icon">
          <FaPhoneVolume />
        </span>
        <div>
          <strong>Quick callback</strong>
          <span>Leave your number, we'll reach out.</span>
        </div>
      </li>
    </ul>
  );

  return (
    <main className="query-page">
      <div className="query-court-lines" aria-hidden="true" />

      <section className="query-hero">
        <div className="query-hero-content">
          <div className="query-badge">
            <FaHeadset />
            Coaching Support
          </div>

          <h1>
            Not sure which trial is right for you?
          </h1>
          <p>
            Share your name and number. Our team will call you back to help with batches,
            nearby centres, trial timing, fees, and enrolment questions.
          </p>

          <div className="query-actions">
            <Link to="/trial-form" className="query-primary-action">
              Book a Trial
              <FaArrowRight />
            </Link>
          </div>

          {renderSupportStrip('query-support-strip-desktop')}
        </div>

        <form className="query-card" onSubmit={handleSubmit} noValidate>
          <div className="query-card-net" aria-hidden="true" />

          <h2>Contact Support</h2>
          <p className="query-card-subtitle">We usually respond during working hours.</p>

          <label className="query-field" htmlFor="query-name">
            <span>Name</span>
            <div className={`query-input-wrap ${errors.userName ? 'has-error' : ''}`}>
              <FaUser className="query-input-icon" />
              <input
                id="query-name"
                type="text"
                value={userName}
                onChange={(event) => {
                  setUserName(event.target.value);
                  if (event.target.value.trim()) {
                    setErrors((previousErrors) => ({ ...previousErrors, userName: '' }));
                  }
                }}
                placeholder="Enter your name"
                autoComplete="name"
              />
            </div>
            {errors.userName && <small role="alert">{errors.userName}</small>}
          </label>

          <label className="query-field" htmlFor="query-phone">
            <span>Phone Number</span>
            <div className={`query-input-wrap ${errors.phoneNumber ? 'has-error' : ''}`}>
              <span className="query-input-prefix">+91</span>
              <input
                id="query-phone"
                type="tel"
                inputMode="numeric"
                value={phoneNumber}
                onChange={handlePhoneChange}
                maxLength={10}
                placeholder="10-digit mobile number"
                autoComplete="tel-national"
              />
            </div>
            {errors.phoneNumber && <small role="alert">{errors.phoneNumber}</small>}
          </label>

          <label className="query-field" htmlFor="query-message">
            <span>How can we help?</span>
            <div className="query-input-wrap query-textarea-wrap">
              <FaCommentDots className="query-input-icon query-input-icon-top" />
              <textarea
                id="query-message"
                value={message}
                onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Tell us about your question"
                rows="3"
              />
            </div>
          </label>

          <button className="query-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="query-spinner" aria-hidden="true" />
                Submitting...
              </>
            ) : (
              'Request a Call Back'
            )}
          </button>

        </form>

        {renderSupportStrip('query-support-strip-mobile')}
      </section>
    </main>
  );
};

export default Query;
